import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import MathText from '../components/MathText';

interface Question {
  id: number;
  question: string;
  board_name: string;
  standard_name: string;
  medium_id?: number;
  medium_name?: string;
  medium_code?: string;
  subject_name: string;
  chapter_name: string;
  topic_name?: string;
  difficulty_level: 'easy' | 'medium' | 'hard';
  status: string;
  marks: number;
  created_at: string;
}

interface Board {
  id: number;
  name: string;
}

interface Standard {
  id: number;
  name: string;
}

interface Subject {
  id: number;
  name: string;
}

interface Chapter {
  id: number;
  name: string;
}

interface Topic {
  id: number;
  name: string;
}

interface Stream {
  id: number;
  name: string;
}

interface Medium {
  id: number;
  code: string;
  name: string;
  native_name: string;
}

const Questions: React.FC = () => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalQuestions, setTotalQuestions] = useState(0);

  // Master data
  const [boards, setBoards] = useState<Board[]>([]);
  const [standards, setStandards] = useState<Standard[]>([]);
  const [streams, setStreams] = useState<Stream[]>([]);
  const [mediums, setMediums] = useState<Medium[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [topics, setTopics] = useState<Topic[]>([]);

  // Filters
  const [boardFilter, setBoardFilter] = useState('');
  const [mediumFilter, setMediumFilter] = useState('');
  const [standardFilter, setStandardFilter] = useState('');
  const [streamFilter, setStreamFilter] = useState('');
  const [subjectFilter, setSubjectFilter] = useState('');
  const [chapterFilter, setChapterFilter] = useState('');
  const [topicFilter, setTopicFilter] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState<Question | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadMasterData();
  }, []);

  useEffect(() => {
    loadQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    rowsPerPage,
    boardFilter,
    mediumFilter,
    standardFilter,
    streamFilter,
    subjectFilter,
    chapterFilter,
    topicFilter,
    difficultyFilter,
    statusFilter,
  ]);

  const loadMasterData = async () => {
    try {
      const [boardsRes] = await Promise.all([api.master.getBoards()]);
      setBoards(boardsRes.data.boards || []);
    } catch (err) {
      console.error('Failed to load master data:', err);
    }
  };

  const loadMediums = async () => {
    try {
      const response = await api.master.getMediums();
      setMediums(response.data.mediums || []);
    } catch (err) {
      console.error('Failed to load mediums:', err);
    }
  };

  const loadStandards = async (boardId: string) => {
    if (!boardId) {
      setStandards([]);
      return;
    }
    try {
      const response = await api.master.getStandards(parseInt(boardId));
      setStandards(response.data.standards || []);
    } catch (err) {
      console.error('Failed to load standards:', err);
    }
  };

  const loadStreams = async (standardId: string) => {
    if (!standardId) {
      setStreams([]);
      return;
    }
    try {
      const response = await api.master.getStreams(parseInt(standardId));
      setStreams(response.data.streams || []);
    } catch (err) {
      console.error('Failed to load streams:', err);
    }
  };

  const loadSubjects = async (boardId: string, standardId: string, mediumId?: string) => {
    if (!boardId || !standardId) {
      setSubjects([]);
      return;
    }
    try {
      const response = await api.master.getSubjects(
        parseInt(boardId),
        parseInt(standardId),
        mediumId ? parseInt(mediumId) : undefined
      );
      setSubjects(response.data.subjects || []);
    } catch (err) {
      console.error('Failed to load subjects:', err);
    }
  };

  const loadChapters = async (subjectId: string) => {
    if (!subjectId) {
      setChapters([]);
      return;
    }
    try {
      const response = await api.master.getChapters(parseInt(subjectId));
      setChapters(response.data.chapters || []);
    } catch (err) {
      console.error('Failed to load chapters:', err);
    }
  };

  const loadTopics = async (chapterId: string) => {
    if (!chapterId) {
      setTopics([]);
      return;
    }
    try {
      const response = await api.master.getTopics(parseInt(chapterId));
      setTopics(response.data.topics || []);
    } catch (err) {
      console.error('Failed to load topics:', err);
    }
  };

  const loadQuestions = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: page + 1,
        limit: rowsPerPage,
      };

      if (boardFilter) params.board_id = boardFilter;
      if (mediumFilter) params.medium_id = mediumFilter;
      if (standardFilter) params.standard_id = standardFilter;
      if (streamFilter) params.stream_id = streamFilter;
      if (subjectFilter) params.subject_id = subjectFilter;
      if (chapterFilter) params.chapter_id = chapterFilter;
      if (topicFilter) params.topic_id = topicFilter;
      if (difficultyFilter) params.difficulty_level = difficultyFilter;
      if (statusFilter) params.status = statusFilter;
      if (searchQuery) params.search = searchQuery;

      const response = await api.questions.getAll(params);
      setQuestions(response.data.questions || []);
      setTotalQuestions(response.data.pagination?.total || 0);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load questions');
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleBoardChange = (value: string) => {
    setBoardFilter(value);
    setMediumFilter('');
    setStandardFilter('');
    setStreamFilter('');
    setSubjectFilter('');
    setChapterFilter('');
    setTopicFilter('');
    setMediums([]);
    setStandards([]);
    setSubjects([]);
    setChapters([]);
    setTopics([]);
    if (value) {
      loadMediums();
      loadStandards(value);
    }
  };

  const handleMediumChange = (value: string) => {
    setMediumFilter(value);
    setSubjectFilter('');
    setChapterFilter('');
    setTopicFilter('');
    setSubjects([]);
    setChapters([]);
    setTopics([]);
    if (boardFilter && standardFilter) {
      loadSubjects(boardFilter, standardFilter, value);
    }
  };

  const handleStandardChange = (value: string) => {
    setStandardFilter(value);
    setStreamFilter('');
    setSubjectFilter('');
    setChapterFilter('');
    setTopicFilter('');
    setSubjects([]);
    setChapters([]);
    setTopics([]);
    loadStreams(value);
    if (boardFilter) {
      loadSubjects(boardFilter, value, mediumFilter);
    }
  };

  const handleSubjectChange = (value: string) => {
    setSubjectFilter(value);
    setChapterFilter('');
    setTopicFilter('');
    loadChapters(value);
  };

  const handleChapterChange = (value: string) => {
    setChapterFilter(value);
    setTopicFilter('');
    loadTopics(value);
  };

  const handleDeleteClick = (question: Question, e: React.MouseEvent) => {
    e.stopPropagation();
    setQuestionToDelete(question);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!questionToDelete) return;

    try {
      setDeleting(true);
      await api.questions.delete(questionToDelete.id);
      setDeleteDialogOpen(false);
      loadQuestions();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete question');
    } finally {
      setDeleting(false);
    }
  };

  const handleSubmitForReview = async (questionId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Submit this question for review?')) return;

    try {
      await api.questions.submitForReview(questionId);
      loadQuestions();
      alert('Question submitted for review successfully');
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to submit question for review');
    }
  };

  const handleRowClick = (questionId: number) => {
    navigate(`/questions/${questionId}`);
  };

  const handleSearch = () => {
    setPage(0);
    loadQuestions();
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy':
        return 'success';
      case 'medium':
        return 'warning';
      case 'hard':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading && questions.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Questions
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage and review all questions in the question bank
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/questions/new')}
        >
          Add Question
        </Button>
      </Box>

      {/* Filters */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Filters
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Board</InputLabel>
            <Select
              value={boardFilter}
              label="Board"
              onChange={(e) => handleBoardChange(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {boards.map((board) => (
                <MenuItem key={board.id} value={board.id}>
                  {board.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }} disabled={!boardFilter}>
            <InputLabel>Medium</InputLabel>
            <Select
              value={mediumFilter}
              label="Medium"
              onChange={(e) => handleMediumChange(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {mediums.map((medium) => (
                <MenuItem key={medium.id} value={medium.id}>
                  {medium.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }} disabled={!boardFilter}>
            <InputLabel>Standard</InputLabel>
            <Select
              value={standardFilter}
              label="Standard"
              onChange={(e) => handleStandardChange(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {standards.map((standard) => (
                <MenuItem key={standard.id} value={standard.id}>
                  {standard.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }} disabled={!standardFilter}>
            <InputLabel>Stream</InputLabel>
            <Select
              value={streamFilter}
              label="Stream"
              onChange={(e) => setStreamFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {streams.map((stream) => (
                <MenuItem key={stream.id} value={stream.id}>
                  {stream.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }} disabled={!standardFilter}>
            <InputLabel>Subject</InputLabel>
            <Select
              value={subjectFilter}
              label="Subject"
              onChange={(e) => handleSubjectChange(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {subjects.map((subject) => (
                <MenuItem key={subject.id} value={subject.id}>
                  {subject.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }} disabled={!subjectFilter}>
            <InputLabel>Chapter</InputLabel>
            <Select
              value={chapterFilter}
              label="Chapter"
              onChange={(e) => handleChapterChange(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {chapters.map((chapter) => (
                <MenuItem key={chapter.id} value={chapter.id}>
                  {chapter.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }} disabled={!chapterFilter}>
            <InputLabel>Topic</InputLabel>
            <Select
              value={topicFilter}
              label="Topic"
              onChange={(e) => setTopicFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {topics.map((topic) => (
                <MenuItem key={topic.id} value={topic.id}>
                  {topic.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Difficulty</InputLabel>
            <Select
              value={difficultyFilter}
              label="Difficulty"
              onChange={(e) => setDifficultyFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="easy">Easy</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="hard">Hard</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="pending_review">Pending Review</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>

        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            size="small"
            placeholder="Search questions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={handleSearchKeyPress}
            sx={{ flexGrow: 1 }}
          />
          <Button variant="contained" startIcon={<SearchIcon />} onClick={handleSearch}>
            Search
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Questions Table */}
      <TableContainer component={Paper} elevation={2}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Question</TableCell>
              <TableCell>Board</TableCell>
              <TableCell>Standard</TableCell>
              <TableCell>Medium</TableCell>
              <TableCell>Subject</TableCell>
              <TableCell>Chapter</TableCell>
              <TableCell>Difficulty</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {questions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    No questions found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              questions.map((question) => (
                <TableRow
                  key={question.id}
                  hover
                  onClick={() => handleRowClick(question.id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Tooltip title={question.question}>
                      <Typography variant="body2" sx={{ maxWidth: 400 }} noWrap>
                        <MathText text={question.question} />
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{question.board_name}</TableCell>
                  <TableCell>{question.standard_name}</TableCell>
                  <TableCell>{question.medium_name || '-'}</TableCell>
                  <TableCell>{question.subject_name}</TableCell>
                  <TableCell>{question.chapter_name}</TableCell>
                  <TableCell>
                    <Chip
                      label={question.difficulty_level}
                      color={getDifficultyColor(question.difficulty_level) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={question.status}
                      color={getStatusColor(question.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="View">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => navigate(`/questions/${question.id}`)}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => navigate(`/questions/${question.id}/edit`)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {question.status === 'rejected' && (
                      <Tooltip title="Submit for Review">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={(e) => handleSubmitForReview(question.id, e)}
                        >
                          <SendIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => handleDeleteClick(question, e)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalQuestions}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Question</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this question? This action cannot be undone.
          </Typography>
          {questionToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {questionToDelete.question}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Questions;
